import type { DocArticle } from './documents.ts';

/** Running assessments, reading what comes back, and the facts only you can supply. */

const ASSESSMENTS: DocArticle = {
  id: 'assessments',
  title: 'Running an assessment',
  summary: 'How a scan starts, what it does, and how it ends.',
  sections: [
    {
      id: 'starting',
      heading: 'Starting one',
      paragraphs: [
        'Open a client and use Run assessment on the Overview tab. Running an assessment needs the scans capability, which owners and engineers hold and viewers do not — everything else on the screen is still readable.',
        'Assessments also start on a schedule, without anyone pressing anything. See Scheduling.',
        'One assessment per client at a time. Asking for a second while one is running is refused, rather than queued behind it, so a double click cannot cost a client two scans.',
      ],
    },
    {
      id: 'stages',
      heading: 'What it does, in order',
      paragraphs: [
        'Collect the configuration, normalise it, evaluate every control, write the plain-English explanations, render the report. The console shows the stage it has reached: Queued, Collecting, Evaluating, Writing narrative.',
        'On a small tenant this takes about three quarters of a minute, and nearly all of it is collection. A large tenant takes longer and we do not promise a ceiling: reading sign-in activity for every account dominates, and it scales with the number of accounts.',
      ],
    },
    {
      id: 'ends',
      heading: 'How it ends',
      paragraphs: [
        'A finished assessment is Complete, Failed or Interrupted. Interrupted is shown as “Interrupted — not a failure”: it means the worker stopped before the assessment did, usually a restart, and it is not evidence about the client.',
        'The console watches a running assessment for five minutes. If nothing has reported back by then it stops waiting and says so — the assessment may still be finishing, and reloading will show whether it did.',
        'A failed assessment records what went wrong against the client. A client whose connection has stopped working shows a notice saying it cannot be assessed again yet, and the stored assessment below it remains a true record of the day it was produced.',
      ],
    },
  ],
};

const FINDINGS: DocArticle = {
  id: 'findings',
  title: 'Reading the findings',
  summary: 'Verdicts, why something could not be assessed, and how to narrow a long list.',
  sections: [
    {
      id: 'verdicts',
      heading: 'The four verdicts',
      paragraphs: [
        'Not met means the collected configuration does not satisfy the control. Met means it does. Not applicable means the thing the control governs does not exist in this tenant. Not evidenced is an open question — we could not determine the answer.',
        'Open questions are neither passes nor failures, and are never counted towards a failure total. Each one says why, and what would settle it.',
      ],
    },
    {
      id: 'why-not-assessed',
      heading: 'Why something could not be assessed',
      paragraphs: [
        'Every open question carries a reason, shown under “Why this could not be assessed”.',
      ],
      bullets: [
        'The tenant’s licence does not expose this configuration.',
        'Our application was not permitted to read this configuration.',
        'Microsoft Graph throttled the read and the retry budget was exhausted.',
        'The configuration could not be collected.',
        'This configuration was not part of the scan.',
        'This depends on a fact only the organisation can supply — see Attestations.',
        'No Microsoft API exposes this; it must be checked by hand.',
        'The capability requires a licence the tenant has not purchased.',
        'The control failed to evaluate — this is a defect in the assessment tool, and worth reporting.',
      ],
    },
    {
      id: 'narrowing',
      heading: 'Narrowing a long list',
      paragraphs: [
        'Findings can be narrowed by Microsoft product, by framework, and by clause within a framework. Each option carries its own count, options that would show nothing are not offered, and choosing a framework first is what unlocks its clauses.',
        'When a filter is in force the counts shown are for that selection only, and the screen says so. Verdict tiles and severity chips are filters too, and evidence can be expanded or collapsed across the whole list.',
        'The frameworks offered are the ones this client’s report actually covers — the catalogue, narrowed by the account’s package, narrowed by the client’s own selection.',
      ],
    },
    {
      id: 'evidence',
      heading: 'Evidence and provenance',
      paragraphs: [
        'Each finding opens to show the evidence that produced it. Values are never truncated in the console. Below the evidence sits the provenance: which control and rule version decided it, whether the evidence was collected or collected and attested, when it was observed, the thresholds applied, and who wrote the explanation.',
        'Where a verdict rests partly on an answer your organisation gave rather than on collected configuration, the finding says so. Everything observable was still checked.',
      ],
    },
  ],
};

const ATTESTATIONS: DocArticle = {
  id: 'attestations',
  title: 'Attestations',
  summary: 'Two controls depend on facts no API exposes. Until they are answered they stay open.',
  sections: [
    {
      id: 'which',
      heading: 'Which controls, and why',
      paragraphs: [
        'Two controls cannot be decided from configuration alone: whether the emergency access accounts are the ones you intend, and which accounts are meant for administration rather than day-to-day work. No Microsoft API knows either. Until they are answered on the client’s Attestations tab, those controls report as open questions — never as met.',
      ],
    },
    {
      id: 'answering',
      heading: 'Answering them',
      paragraphs: [
        'Each answer is Yes, No or Not yet known, with optional account object ids, a note, and the name of whoever is attesting. The name is required: an attestation is a statement by a person, and the report says who made it.',
      ],
      bullets: [
        'The object ids of the designated emergency access accounts.',
        'Whether the break-glass procedure and credential custody are written down and current.',
        'The object ids of accounts intended for administration only.',
      ],
    },
    {
      id: 'attestation-effect',
      heading: 'What an answer changes',
      paragraphs: [
        'Once the emergency accounts are named, DriftWatch checks them against the directory: that they exist, are enabled, hold Global Administrator permanently, are cloud-only, and are excluded from every enabled Conditional Access policy. An answer does not make a control pass — it makes it assessable.',
        'The nominated emergency accounts are also excluded from the dormant-account control, so a correct break-glass setup does not report as an offboarding failure.',
      ],
    },
  ],
};

const THRESHOLDS: DocArticle = {
  id: 'thresholds',
  title: 'Thresholds',
  summary: 'The numbers a control compares against, and where a client-specific one can be set.',
  sections: [
    {
      id: 'threshold-basics',
      heading: 'What they are',
      paragraphs: [
        'Some controls compare against a number — how many emergency accounts to expect, how many days of inactivity makes an account dormant. The catalogue ships a default for each, and a client can carry an override.',
        'Whichever value was in force is recorded on every finding it produced, and shown as “Thresholds applied”. A reader can always see which number produced the verdict.',
      ],
    },
    {
      id: 'where',
      heading: 'Where to see and set them',
      paragraphs: [
        'The client’s Thresholds tab lists every control that takes one, with the catalogue default beside any override in force.',
        'The tab is read-only today. Setting an override is an API operation, and it is validated when it is written rather than when it is used — an override that silently did nothing would be worse than a rejected request, because you would believe a threshold was in force when the report had been computed with another.',
      ],
    },
    {
      id: 'comparability',
      heading: 'Changing one breaks comparison',
      paragraphs: [
        'If a threshold changes between two assessments, those assessments are not comparable for that control, and the product says so rather than reporting a change in the client. The change was ours, or yours, but it was not theirs.',
      ],
    },
  ],
};

export const ASSESSING: readonly DocArticle[] = [ASSESSMENTS, FINDINGS, ATTESTATIONS, THRESHOLDS];
